import { useEffect, useRef, useState, useCallback } from "react";
import { jsPDF } from "jspdf";
import "svg2pdf.js";
import { layoutAbc, ZOOM_STOPS } from "../lib/abcLayout";
import { ChevronLeft, Download, ZoomIn, RectangleVertical, RectangleHorizontal, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

// A4 in CSS px @96dpi
const A4 = {
  portrait: { w: 794, h: 1123 },
  landscape: { w: 1123, h: 794 },
};
const MARGIN = 36;

export const PdfExportDialog = ({ open, onOpenChange, tune, abc }) => {
  const measureRef = useRef(null); // hidden live abcjs render (source for svg2pdf)
  const linesRef = useRef([]); // live <svg> nodes, one per staff line
  const scrollRef = useRef(null);
  const [orient, setOrient] = useState("portrait");
  const [zoom, setZoom] = useState(100);
  const [pages, setPages] = useState([]); // [{ lines: [{ html, w, h }] }]
  const [busy, setBusy] = useState(false);
  const [availW, setAvailW] = useState(360);

  const page = A4[orient];
  const contentW = page.w - MARGIN * 2;
  const pageContentH = page.h - MARGIN * 2;
  // preview scales to fit the dialog width so it is never clipped (portrait or landscape)
  const scale = Math.min(availW, 900) / page.w;

  // Zoom stops are module-level (ZOOM_STOPS) so the layout callbacks stay stable.
  const zoomIdx = Math.max(0, ZOOM_STOPS.indexOf(zoom));
  const S = zoom / 100;

  // Render the tune at a given zoom into the hidden container and paginate it.
  const layout = useCallback(
    (zoomVal) => {
      if (!open || !measureRef.current) return null;
      try {
        return layoutAbc(measureRef.current, abc, contentW, pageContentH, zoomVal);
      } catch (e) {
        /* eslint-disable no-console */
        console.warn("abc render failed", e);
        return null;
      }
    },
    [open, abc, contentW, pageContentH]
  );

  const render = useCallback(() => {
    const r = layout(zoom);
    if (!r) return;
    linesRef.current = r.svgs;
    setPages(r.built.length ? r.built : [{ lines: [] }]);
  }, [layout, zoom]);

  // Biggest notation that still fits the page count the user is currently on.
  const fitPage = useCallback(() => {
    const target = Math.max(1, pages.length || 1);
    for (let i = ZOOM_STOPS.length - 1; i >= 0; i--) {
      const r = layout(ZOOM_STOPS[i]);
      if (r && r.built.length <= target) {
        if (ZOOM_STOPS[i] === zoom) {
          linesRef.current = r.svgs;
          setPages(r.built);
        } else {
          setZoom(ZOOM_STOPS[i]);
        }
        return;
      }
    }
    render();
  }, [layout, zoom, pages.length, render]);

  useEffect(() => {
    const t = setTimeout(render, 120);
    return () => clearTimeout(t);
  }, [render]);

  useEffect(() => {
    if (!open) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setAvailW(Math.max(200, el.clientWidth - 32));
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [open, orient]);

  const download = async () => {
    const svgs = linesRef.current;
    if (!svgs.length) return;
    setBusy(true);
    const filename = `${tune.name.replace(/[^a-z0-9]+/gi, "_")}_${orient}.pdf`;
    toast.info("Preparing PDF…");
    try {
      const doc = new jsPDF({
        unit: "px",
        format: [A4.portrait.w, A4.portrait.h],
        orientation: orient,
        hotfixes: ["px_scaling"],
      });
      // walk the SAME pagination used by the preview
      let idx = 0;
      for (let p = 0; p < pages.length; p++) {
        if (p > 0) doc.addPage([A4.portrait.w, A4.portrait.h], orient);
        let y = MARGIN;
        for (const line of pages[p].lines) {
          const svg = svgs[idx++];
          if (!svg) continue;
          // eslint-disable-next-line no-await-in-loop
          await doc.svg(svg, { x: MARGIN, y, width: contentW, height: line.h });
          y += line.h;
        }
      }
      doc.save(filename);
      toast.success("PDF ready");
    } catch (e) {
      /* eslint-disable no-console */
      console.warn("pdf export failed", e);
      toast.error("Could not generate the PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-[#fdfbf7]" data-testid="pdf-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-full border border-[#e5e0d8] bg-white p-1.5 text-[#2d2824] hover:bg-[#f3efea]"
              data-testid="pdf-back"
              aria-label="back to tune"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <DialogTitle className="font-serif text-2xl">Export “{tune.name}”</DialogTitle>
          </div>
          <DialogDescription>
            Print preview · {pages.length} A4 page{pages.length > 1 ? "s" : ""} ({orient}) · rows
            follow the tune’s own line breaks
          </DialogDescription>
        </DialogHeader>

        {/* orientation toggle + wide zoom slider */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setOrient((o) => (o === "portrait" ? "landscape" : "portrait"))}
            className="shrink-0 rounded-full border-2 border-[#2d2824] bg-[#2d2824] p-2.5 text-white transition-transform active:scale-90"
            data-testid="orient-toggle"
            title={orient === "portrait" ? "Switch to landscape" : "Switch to portrait"}
            aria-label="toggle orientation"
          >
            {orient === "portrait" ? (
              <RectangleVertical className="h-5 w-5" />
            ) : (
              <RectangleHorizontal className="h-5 w-5" />
            )}
          </button>
          <button
            onClick={fitPage}
            className="flex shrink-0 items-center gap-1.5 rounded-full border-2 border-[#2d2824] px-3 py-2 text-xs font-extrabold text-[#2d2824] transition-transform active:scale-95"
            data-testid="pdf-fit"
            title={`Fill ${pages.length} page${pages.length > 1 ? "s" : ""} as much as possible`}
          >
            <Maximize2 className="h-4 w-4" /> Fit {pages.length}p
          </button>
          <ZoomIn className="h-4 w-4 shrink-0 text-[#2d2824]" />
          <span className="w-11 shrink-0 text-right text-sm font-bold text-[#2d2824]">{zoom}%</span>
        </div>

        <input
          type="range"
          min={0}
          max={ZOOM_STOPS.length - 1}
          step={1}
          value={zoomIdx}
          onChange={(e) => setZoom(ZOOM_STOPS[Number(e.target.value)])}
          className="fame-range w-full"
          style={{
            "--pct": `${(zoomIdx / (ZOOM_STOPS.length - 1)) * 100}%`,
            "--c": "#2d2824",
          }}
          data-testid="pdf-zoom"
        />

        {/* real print preview — one framed A4 sheet per page, identical to the PDF */}
        <div ref={scrollRef} className="flex max-h-[46vh] flex-col items-center gap-4 overflow-auto rounded-2xl bg-[#d8d0c3] p-4">
          {pages.map((pg, i) => (
            <div key={i} className="relative shrink-0">
              <div
                style={{
                  width: page.w * scale,
                  height: page.h * scale,
                  background: "#fff",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
                }}
              >
                <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: page.w }}>
                  <div style={{ padding: MARGIN, boxSizing: "border-box", width: page.w }}>
                    {pg.lines.map((ln, j) => (
                      <div key={j} style={{ width: contentW, height: ln.h, overflow: "hidden" }}>
                        <div
                          style={{ transform: `scale(${ln.s})`, transformOrigin: "top left" }}
                          dangerouslySetInnerHTML={{ __html: ln.html }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <span
                style={{
                  position: "absolute", right: 6, bottom: 6, fontSize: 10, fontWeight: 800,
                  color: "#2d2824", background: "rgba(255,255,255,0.85)", padding: "1px 6px", borderRadius: 6,
                }}
              >
                {i + 1}/{pages.length}
              </span>
            </div>
          ))}
        </div>

        {/* hidden live source for svg2pdf */}
        <div ref={measureRef} style={{ position: "absolute", left: -99999, top: 0, width: contentW }} aria-hidden />

        <button
          onClick={download}
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2d2824] px-6 py-3.5 text-base font-extrabold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
          data-testid="pdf-download"
        >
          <Download className="h-5 w-5" /> {busy ? "Generating…" : "Download PDF"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
